import React from 'react';
import { MODAL_STYLES } from '../../constants/styles';
import { allergenConflictLabel } from '../../utils/allergenLabels';
import ProteinSubstitutionSection from '../Substitution/ProteinSubstitutionSection';
import VeggieSubstitutionSection from '../Substitution/VeggieSubstitutionSection';
import StarchSubstitutionSection from '../Substitution/StarchSubstitutionSection';
import SauceSubstitutionSection from '../Substitution/SauceSubstitutionSection';
import GarnishSubstitutionSection from '../Substitution/GarnishSubstitutionSection';

const MealModal = ({
    meal,
    onClose,
    modalRef,
    saving,
    onProteinSubstitution,
    onVeggieSubstitution,
    onSauceSubstitution,
    onStarchSubstitution,
    onToggleGarnish,
    isLocked,
    showAddedBanner = false,
    billingChanges,
    deliveryDates,
    selectedDeliveryDate,
    onAddToCart,
    addToCartLabel = 'Add to Plan',
    isDraft = false
}) => {
    const allergenNames = allergenConflictLabel(meal.allergenConflicts);

    // Only surface auto-substitution badges while the dish is first being
    // configured (showAddedBanner is set on add) — reopening an already-added
    // dish shouldn't re-show them.
    const findAutoSub = (category) => showAddedBanner
        ? (meal.autoSubstitutions || []).find(s => s.category === category)
        : undefined;

    const renderActionBar = ({ compact } = {}) => (
        <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: compact ? '6px' : '10px',
            ...(compact ? {} : { marginTop: '18px', paddingTop: '16px', borderTop: '1px solid #eee' })
        }}>
            <button
                type="button"
                onClick={onClose}
                disabled={saving}
                style={{
                    padding: compact ? '5px 12px' : '11px 22px',
                    fontSize: compact ? '12px' : '14px',
                    fontWeight: '600',
                    borderRadius: compact ? '6px' : '8px',
                    border: '1.5px solid #ccc',
                    background: '#fff',
                    color: '#555',
                    cursor: saving ? 'not-allowed' : 'pointer'
                }}
            >
                Cancel
            </button>
            <button
                type="button"
                onClick={onAddToCart}
                disabled={saving || isLocked}
                style={{
                    padding: compact ? '5px 14px' : '11px 26px',
                    fontSize: compact ? '12px' : '14px',
                    fontWeight: '700',
                    borderRadius: compact ? '6px' : '8px',
                    border: 'none',
                    background: '#f9a826',
                    color: '#fff',
                    cursor: (saving || isLocked) ? 'not-allowed' : 'pointer'
                }}
            >
                {addToCartLabel}
            </button>
        </div>
    );

    const actionBar = onAddToCart ? renderActionBar() : null;
    // Mirror the buttons up top too, so a long ingredient list doesn't hide the
    // only way to confirm the dish.
    const topActionBar = onAddToCart ? renderActionBar({ compact: true }) : null;

    return (
        <div
            className="meal-modal-container"
            style={MODAL_STYLES.overlay}
            onClick={onClose}
        >
            <div
                ref={modalRef}
                className='meal-modal'
                style={MODAL_STYLES.content}
                onClick={(e) => e.stopPropagation()}
            >

                {/* A draft closes via the action bar's Cancel; the plain × is for an
                    order already in the plan, whose edits apply as you click. */}
                {!onAddToCart && (
                    <button
                        onClick={onClose}
                        style={MODAL_STYLES.closeButton}
                        title="Close"
                    >
                        ×
                    </button>
                )}

                <div style={{ padding: '20px' }}>
                    {topActionBar && (
                        <div style={{ marginBottom: '10px' }}>
                            {topActionBar}
                        </div>
                    )}
                    <div
                        className="meal-modal-content"
                        style={{
                            display: 'flex',
                            gap: '20px',

                            alignItems: 'flex-start'
                        }}>
                        {/* Image section */}
                        <div
                            className="meal-modal-image"
                            style={{
                                flex: '0 0 300px',
                                minHeight: '200px'
                            }}>
                            {meal.imageUrl && (
                                <img
                                    src={meal.imageUrl}
                                    alt={meal.itemName}
                                    style={{
                                        width: '100%',
                                        height: '200px',
                                        objectFit: 'cover',
                                        borderRadius: '8px'
                                    }}
                                />
                            )}
                        </div>

                        {/* Details section */}
                        <div
                            className="meal-modal-details"
                            style={{
                                flex: '1',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '15px'
                            }}>
                            {/* Title */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start'
                            }}>
                                <div>
                                    <h4 style={{ margin: '0 0 5px 0', color: '#333' }}>
                                        {meal.itemName}
                                        {meal.itemName.length > 30 && (
                                            <div />
                                        )}
                                        {(meal.quantity === 0 || isDraft) && (
                                            <span style={{
                                                width: 'fit-content',
                                                margin: '0 8px 8px 0',
                                                fontSize: '12px',
                                                backgroundColor: '#e3f2fd',
                                                color: '#1976d2',
                                                padding: '2px 6px',
                                                borderRadius: '3px'
                                            }}>
                                                {isDraft ? 'Not added yet' : 'Available to Add'}
                                            </span>
                                        )}
                                    </h4>
                                    {meal.quantity === 0 && (
                                        <div style={{
                                            backgroundColor: '#e3f2fd',
                                            border: '1px solid #bbdefb',
                                            borderRadius: '6px',
                                            padding: '12px',
                                            fontSize: '14px',
                                            color: '#1565c0'
                                        }}>
                                            <strong>Available Dish:</strong> This dish is available for this week but not currently in your meal plan. Click "Add" to add it to your orders!
                                        </div>
                                    )}

                                    {/* Whenever what's selected right now clashes with the
                                        customer's allergies — a dish that couldn't be adjusted,
                                        or an ingredient picked by hand. Shown as selected rather
                                        than half-stripped, so the customer decides on the real
                                        recipe. */}
                                    {meal.hasAllergenConflict && (
                                        <div style={{
                                            marginTop: '5px',
                                            backgroundColor: '#ffeaa7',
                                            border: '1px solid #fdcb6e',
                                            borderRadius: '6px',
                                            padding: '5px',
                                            fontSize: '12px',
                                            color: '#a1651b'
                                        }}>
                                            ⚠️ Allergy detected<strong>{allergenNames ? `: ${allergenNames}` : ' in this meal'}.</strong>{' '}
                                        </div>
                                    )}
                                    {(meal.meal === 'Add On' || meal.meal === 'Snack' || meal.meal === 'Breakfast') && meal.price > 0 && <label style={{
                                        display: 'block',
                                        margin: '8px 0 0 0',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        color: '#555'
                                    }}>
                                        Price: ${meal.price}
                                    </label>
                                    }



                                </div>
                            </div>
                            {/* Show substitution options for ordered dishes only */}
                            {meal.quantity > 0 && (

                                <>
                                    {meal.proteinOptions.length > 0 && (
                                        <ProteinSubstitutionSection
                                            order={meal}
                                            orderIndex={meal.actualIndex}
                                            onProteinSubstitution={onProteinSubstitution}
                                            isLocked={isLocked}
                                            saving={saving}
                                            billingChanges={billingChanges}
                                            deliveryDates={deliveryDates}
                                            selectedDeliveryDate={selectedDeliveryDate}
                                            autoSubstitution={findAutoSub('protein')}
                                        />)}

                                    {meal.veggieOptions.length > 0 && (
                                        <VeggieSubstitutionSection
                                            order={meal}
                                            orderIndex={meal.actualIndex}
                                            onVeggieSubstitution={onVeggieSubstitution}
                                            isLocked={isLocked}
                                            saving={saving}
                                            autoSubstitution={findAutoSub('veggie')}
                                        />
                                    )}
                                    {meal.starchOptions.length > 0 && (
                                        <>
                                            <StarchSubstitutionSection
                                                order={meal}
                                                orderIndex={meal.actualIndex}
                                                onStarchSubstitution={onStarchSubstitution}
                                                isLocked={isLocked}
                                                saving={saving}
                                                autoSubstitution={findAutoSub('starch')}
                                            />
                                        </>
                                    )}
                                    {meal.sauceOptions.length > 0 && (
                                        <SauceSubstitutionSection
                                            order={meal}
                                            orderIndex={meal.actualIndex}
                                            onSauceSubstitution={onSauceSubstitution}
                                            isLocked={isLocked}
                                            saving={saving}
                                            autoSubstitution={findAutoSub('sauce')}
                                        />
                                    )}
                                    {meal.garnishOptions.length > 0 && (
                                        <GarnishSubstitutionSection
                                            order={meal}
                                            orderIndex={meal.actualIndex}
                                            onToggleGarnish={onToggleGarnish}
                                            isLocked={isLocked}
                                            saving={saving}
                                            autoSubstitution={findAutoSub('garnish')}
                                        />
                                    )}


                                    {/* Ingredients section for snacks */}
                                    {/* {meal.meal === 'Snack' && (meal.originalIngredients && meal.originalIngredients.length === 1) && (
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                marginBottom: '8px',
                                                fontSize: '14px',
                                                fontWeight: 'bold',
                                                color: '#555'
                                            }}>
                                                All Ingredients:
                                            </label>
                                            <div
                                                className="ingredient-buttons"
                                                style={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: '8px'
                                                }}>
                                                {meal.originalIngredients.map((ingredient, ingredientIndex) => {
                                                    const isActive = meal.finalIngredients.includes(ingredient.id);
                                                    return (
                                                        <button
                                                            key={ingredientIndex}
                                                            className="ingredient-button"
                                                            onClick={() => onToggleIngredient(meal.actualIndex, ingredient.name, isActive)}
                                                            disabled={saving}
                                                            style={getIngredientButtonStyle(isActive, saving)}
                                                            title={isActive ? `Remove ${ingredient.name}` : `Add ${ingredient.name}`}
                                                        >
                                                            <span style={{
                                                                marginRight: '4px',
                                                                fontSize: '12px'
                                                            }}>
                                                                {isActive ? '✓' : '+'}
                                                            </span>
                                                            {ingredient.name}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )} */}
                                </>
                            )}

                            {/* Show ingredients preview for available dishes */}
                            {meal.quantity === 0 && (
                                <div>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '8px',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        color: '#555'
                                    }}>
                                        What's included:
                                    </label>
                                    <div style={{
                                        fontSize: '14px',
                                        color: '#666',
                                        lineHeight: '1.6'
                                    }}>
                                        {(() => {
                                            const categories = [
                                                { label: 'Protein', options: meal.proteinOptions, showSubs: true },
                                                { label: 'Starch', options: meal.starchOptions, showSubs: true },
                                                { label: 'Vegetables', options: meal.veggieOptions, showSubs: false },
                                                { label: 'Sauce', options: meal.sauceOptions, showSubs: true },
                                                { label: 'Garnish', options: meal.garnishOptions, showSubs: false },
                                            ];
                                            const originalIds = new Set((meal.originalIngredients || []).map(i => i.id));
                                            const lines = categories
                                                .map(({ label, options, showSubs }) => {
                                                    if (!options || !Array.isArray(options)) return null;
                                                    // Allergy adjustment already resolved a selection for
                                                    // available dishes — mirror what the card shows rather
                                                    // than the unadjusted recipe. id null is "No Sauce"/
                                                    // "No Starch": a selection, not an ingredient to list.
                                                    const display = meal.selectionResolved
                                                        ? options.filter(o => o.isActive && o.id != null)
                                                        : options.filter(o => originalIds.has(o.id));
                                                    if (display.length === 0) return null;
                                                    const stripSF = (s) => (s || '').replace(/^SF /, '');
                                                    const namesList = display.map(o => {
                                                        const cleanSubs = (o.subIngredientNames || []).map(stripSF);
                                                        const subs = showSubs && cleanSubs.length > 0
                                                            ? ` (${cleanSubs.join(', ')})`
                                                            : '';
                                                        return { name: stripSF(o.name?.trim()), subs, rawSubs: cleanSubs };
                                                    });
                                                    return { label, namesList };
                                                })
                                                .filter(Boolean);

                                            if (lines.length === 0) {
                                                return meal.originalIngredients?.length > 0
                                                    ? meal.originalIngredients.map(i => i.name.trim()).join(', ')
                                                    : 'Original Ingredients Not Listed';
                                            }

                                            const singleCategory = lines.length === 1;
                                            if (singleCategory) {
                                                return lines[0].namesList.map((item, i) => {
                                                    const nameL = item.name?.toLowerCase() || '';
                                                    const titleL = meal.itemName?.toLowerCase() || '';
                                                    const sameName = nameL === titleL || titleL.includes(nameL) || nameL.includes(titleL);
                                                    const subsText = item.subs || (item.rawSubs.length > 0 ? ` (${item.rawSubs.join(', ')})` : '');
                                                    if (sameName && !subsText) {
                                                        return null;
                                                    }
                                                    return <div key={i}><span style={{ fontWeight: '600' }}>{item.name}</span>{subsText}</div>;
                                                });
                                            }
                                            return lines.map((line, i) => (
                                                <div key={i}>
                                                    <span style={{ fontWeight: '600' }}>{line.label}:</span> {line.namesList.map(item => item.name + item.subs).join(', ')}
                                                </div>
                                            ));
                                        })()}
                                    </div>

                                </div>
                            )}
                        </div>
                    </div>

                    {/* Bottom action bar */}
                    {actionBar}
                </div>

            </div>
        </div >
    );
};

export default MealModal;